/**
 * InitiativesRepo - persistence for initiatives with legacy key compatibility
 *
 * Single place that reads/writes initiatives to storage and legacy keys.
 * Keeps Strategic Intelligence v5.x modules and any older views in sync.
 */

import StorageService from '../../services/StorageService.js';
import db, { migrateInitiativesIfEmpty } from '../db/DexieDB.js';

const PRIMARY_KEY = 'initiatives';
const LEGACY_KEY = 'goalsDatabase';

class InitiativesRepo {
  /** Determine if IndexedDB should be used for reads */
  useIndexedDb() {
    try {
      if (typeof window !== 'undefined' && window.__USE_INDEXED_DB__) return true;
      if (typeof localStorage !== 'undefined' && localStorage.getItem('sip_useIndexedDb') === 'true') return true;
    } catch(_) {}
    try {
      if (import.meta.env?.DEV) return true; // default ON in development
      return import.meta.env?.VITE_USE_INDEXEDDB === 'true';
    } catch(_) { return false; }
  }

  /**
   * Load initiatives array from storage.
   * Prefers PRIMARY_KEY, falls back to LEGACY_KEY, defaults to [].
   */
  load() {
    try {
      const primary = StorageService.get(PRIMARY_KEY);
      if (Array.isArray(primary)) return primary;
    } catch (_) {}

    try {
      const raw = localStorage.getItem(PRIMARY_KEY) || localStorage.getItem(LEGACY_KEY);
      if (raw) return JSON.parse(raw);
    } catch (_) {}

    return [];
  }

  /**
   * Load initiatives from IndexedDB (async). Falls back to load() on error.
   */
  async loadIndexed() {
    try {
      await migrateInitiativesIfEmpty();
      const rows = await db.initiatives.toArray();
      if (Array.isArray(rows) && rows.length) return rows;
    } catch (e) {
      console.warn('Dexie read failed; falling back to legacy storage', e);
    }
    return this.load();
  }

  /**
   * Save full initiatives array to both primary and legacy keys.
   */
  save(all) {
    try { StorageService.set(PRIMARY_KEY, all); } catch (_) {}
    try {
      localStorage.setItem(PRIMARY_KEY, JSON.stringify(all));
      localStorage.setItem(LEGACY_KEY, JSON.stringify(all));
    } catch (_) {}

    // Dual-write to IndexedDB (best effort, async)
    (async () => {
      try {
        await migrateInitiativesIfEmpty();
        if (!Array.isArray(all)) return;
        const sanitizeId = (val) => {
          if (typeof val === 'string' && val.trim()) return val.trim();
          if (typeof val === 'number' && !Number.isNaN(val)) return String(val);
          return `init_${Date.now()}_${Math.random().toString(36).slice(2,9)}`;
        };
        const rows = all.map((i) => ({
          ...i,
          id: sanitizeId(i?.id),
          createdAt: i?.createdAt || new Date().toISOString(),
          updatedAt: i?.updatedAt || new Date().toISOString()
        }));
        await db.transaction('rw', db.initiatives, async () => {
          await db.initiatives.clear();
          try {
            await db.initiatives.bulkPut(rows);
          } catch (e) {
            // Fallback: sanitize ids and retry one-by-one to skip invalid rows
            console.warn('Dexie bulkPut failed, retrying per-row with sanitized ids', e);
            for (const r of rows) {
              try {
                const clean = { ...r, id: sanitizeId(r?.id) };
                await db.initiatives.put(clean);
              } catch (_) {}
            }
          }
        });
      } catch (e) {
        console.warn('Dexie dual-write failed:', e);
      }
    })();

    return true;
  }

  /**
   * Remove all initiatives from storage (primary + legacy).
   */
  clear() {
    try { StorageService.remove(PRIMARY_KEY); } catch (_) {}
    try { StorageService.remove(LEGACY_KEY); } catch (_) {}
    try {
      localStorage.removeItem(PRIMARY_KEY);
      localStorage.removeItem(LEGACY_KEY);
    } catch (_) {}
    // Clear IndexedDB table (best effort)
    (async () => {
      try { await db.initiatives.clear(); } catch (_) {}
    })();
    return true;
  }
}

const initiativesRepo = new InitiativesRepo();
export default initiativesRepo;
export { initiativesRepo };
